<script lang="ts" setup>
import { MobilePanelPosition } from '~/static/enums/mobile-panel-position'

interface Props {
  selectedId: number | string
  title: string
  showBackButton: boolean
  showCloseButton: boolean
  maxPosition: MobilePanelPosition
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'back'): void
  (e: 'change-height', height: number): void
}>()

const prevPosition = ref(MobilePanelPosition.Small)
const position = ref(MobilePanelPosition.Small)
const height = ref(0)

provide('mobilePanelPosition', readonly(position))

const direction = ref<'down' | 'up'>('up')
const lastChangeDirectionPointY = ref(0)
const prevTouchY = ref(0)
const onTouchStart = (e: TouchEvent) => {
  prevTouchY.value = e.changedTouches[0].clientY
  lastChangeDirectionPointY.value = e.changedTouches[0].clientY
}
const onTouchMove = (e: TouchEvent) => {
  let lastDirection = direction.value
  if (prevTouchY.value - e.changedTouches[0].clientY > 0) {
    lastDirection = 'up'
  } else if (prevTouchY.value - e.changedTouches[0].clientY < 0) {
    lastDirection = 'down'
  }
  if (lastDirection !== direction.value) {
    direction.value = lastDirection
    lastChangeDirectionPointY.value = e.changedTouches[0].clientY
  }
  prevTouchY.value = e.changedTouches[0].clientY
  height.value = Math.max(Math.min(window.innerHeight - e.changedTouches[0].clientY, maxHeight.value), minHeight.value)
  nextTick(() => {
    emit('change-height', height.value)
  })
}
const onTouchEnd = (e: TouchEvent) => {
  if (Math.abs(lastChangeDirectionPointY.value - e.changedTouches[0].clientY) >= 10) {
    if (direction.value === 'up') {
      position.value = props.maxPosition
    } else {
      position.value = MobilePanelPosition.Small
    }
  }
  prevPosition.value = position.value
  recalculatePanelHeight()
}

const recalculatePanelHeight = () => {
  document.body.scrollTo({ top: 0 })
  height.value = getHeightByPostion(position.value)
  nextTick(() => {
    emit('change-height', height.value)
  })
}

const getHeightByPostion = (pos: MobilePanelPosition): number => {
  const visibleHeight = window.innerHeight + (window.visualViewport?.offsetTop ?? 0)
  if (pos === MobilePanelPosition.Small) {
    return visibleHeight * 0.25
  }
  if (pos === MobilePanelPosition.Opened) {
    return visibleHeight * 0.5
  }
  return visibleHeight - 24
}

const maxHeight = computed(() => {
  return getHeightByPostion(props.maxPosition)
})

const minHeight = computed(() => {
  return getHeightByPostion(MobilePanelPosition.Small)
})

const inputFocused = ref(false)

const openMaxPanelOnInput = (e: FocusEvent) => {
  const activeTag = (e.target as HTMLElement)?.tagName.toLowerCase()
  if (
    (activeTag === 'input' || activeTag === 'textarea') &&
    (e.target as HTMLElement).closest('.mobile-panel') !== null &&
    (e.target as HTMLInputElement).type !== 'checkbox'
  ) {
    prevPosition.value = position.value
    position.value = MobilePanelPosition.Max
    inputFocused.value = true
    recalculatePanelHeight()
  }
}
const revertPositionOnInputEnd = (e: FocusEvent) => {
  const activeTag = (e.target as HTMLElement)?.tagName.toLowerCase()
  if (
    (activeTag === 'input' || activeTag === 'textarea') &&
    (e.target as HTMLElement).closest('.mobile-panel') !== null &&
    (e.target as HTMLInputElement).type !== 'checkbox'
  ) {
    revertPosition()
  }
}
const revertPosition = () => {
  position.value = prevPosition.value
  inputFocused.value = false
  recalculatePanelHeight()
}

const fullPanelEditor = ref(false)
const fullPanelTitle = ref<string | null>(null)
const fullPanelShowBackButton = ref<boolean | null>(null)
const fullPanelShowCloseButton = ref<boolean | null>(null)
const fullPanelFreezedHeight = ref<number | null>(null)

const open = () => {
  if (position.value === MobilePanelPosition.Small) {
    prevPosition.value = props.maxPosition
    position.value = props.maxPosition
    recalculatePanelHeight()
  }
}
const collapse = () => {
  prevPosition.value = MobilePanelPosition.Small
  position.value = MobilePanelPosition.Small
  recalculatePanelHeight()
}

const openFullPanelEditor = (
  fullPanelProps: {
    title: string
    showBackButton: boolean
    showCloseButton: boolean
    freezedHeight?: number
  },
) => {
  fullPanelEditor.value = true
  fullPanelTitle.value = fullPanelProps.title
  fullPanelShowBackButton.value = fullPanelProps.showBackButton
  fullPanelShowCloseButton.value = fullPanelProps.showCloseButton
  fullPanelFreezedHeight.value = fullPanelProps.freezedHeight ?? null
}
const closeFullPanelEditor = () => {
  fullPanelEditor.value = false
  fullPanelTitle.value = null
  fullPanelShowBackButton.value = null
  fullPanelShowCloseButton.value = null
  fullPanelFreezedHeight.value = null
  $event('full-panel:closed')
}

const back = () => {
  if (fullPanelEditor.value) {
    closeFullPanelEditor()
    return
  }
  emit('back')
}
const close = () => {
  if (fullPanelEditor.value) {
    closeFullPanelEditor()
    return
  }
  emit('close')
}

const { $event, $listen, $off } = useNuxtApp()

onMounted(() => {
  recalculatePanelHeight()
  document.addEventListener('focusin', openMaxPanelOnInput)
  document.addEventListener('focusout', revertPositionOnInputEnd)
  window.addEventListener('resize', recalculatePanelHeight)
  window.visualViewport?.addEventListener('resize', recalculatePanelHeight)
  window.visualViewport?.addEventListener('scroll', recalculatePanelHeight)
  $listen('full-panel:open', openFullPanelEditor)
  $listen('full-panel:close', closeFullPanelEditor)
})

onBeforeUnmount(() => {
  document.removeEventListener('focusin', openMaxPanelOnInput)
  document.removeEventListener('focusout', revertPositionOnInputEnd)
  window.removeEventListener('resize', recalculatePanelHeight)
  window.visualViewport?.removeEventListener('resize', recalculatePanelHeight)
  window.visualViewport?.removeEventListener('scroll', recalculatePanelHeight)
  $off('full-panel:open', openFullPanelEditor)
  $off('full-panel:close', closeFullPanelEditor)
})

watch(() => {
  return props.maxPosition
}, (newValue, oldValue) => {
  if (position.value === oldValue) {
    position.value = newValue
    recalculatePanelHeight()
  }
})

const contentRenderingKey = ref(0)

watch(() => {
  return props.selectedId
}, () => {
  if (fullPanelEditor.value) {
    closeFullPanelEditor()
    contentRenderingKey.value++
  }
  nextTick(() => {
    document.querySelector('#mobile-panel')?.querySelector('.content-body')?.scrollTo({ top: 0 })
  })
})

defineExpose({
  collapse,
})

</script>

<template>
  <div
    id="mobile-panel"
    class="mobile-panel"
    :class="position === MobilePanelPosition.Max && !fullPanelFreezedHeight ? 'mobile-panel--full' : ''"
  >
    <div
      class="mobile-panel__content"
      :style="{ height: (fullPanelFreezedHeight ?? height) + 'px' }"
    >
      <div
        class="content-header prevent_unselection"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="d-flex align-items-start">
          <button
            v-if="fullPanelShowBackButton === true || (fullPanelShowBackButton === null && showBackButton)"
            class="mobile-panel__back"
            @click.prevent="back"
          >
            <bootstrap-icon-arrow-left />
          </button>
          <h5 class="content-header-title">
            {{ fullPanelTitle ?? title }}
          </h5>
        </div>
        <div
          v-if="fullPanelShowCloseButton === true || (fullPanelShowCloseButton === null && showCloseButton)"
          class="d-flex gap-2"
        >
          <slot
            name="close-button"
            :close="close"
          >
            <button
              class="mobile-panel__close mobile-panel__close--icon"
              @click.prevent="close"
            >
              <bootstrap-icon-x-lg />
            </button>
          </slot>
        </div>
      </div>
      <div
        v-if="fullPanelEditor"
        class="content-body"
        id="full-panel-editor"
        :style="{ height: ((fullPanelFreezedHeight ?? height) - 48) + 'px' }"
      />
      <div
        v-show="!fullPanelEditor"
        class="content-body"
        id="mobile-scrollable-content"
        :style="{ height: (height - 48) + 'px' }"
        :key="contentRenderingKey"
        @scroll="open"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.mobile-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: #EBECF0;

  @include media-breakpoint-up(lg) {
    display: none;
  }
  
  &--full {
    background: rgba(0, 0, 0, .5);
    top: 0;
  }

  &__content {
    background: $white;
    border-radius: rem(4px) rem(4px) 0 0;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      width: rem(156px);
      height: rem(4px);
      top: rem(2px);
      left: 50%;
      transform: translateX(-50%);
      background: #EBECF0;
      border-radius: rem(7px);
      
      @include media-breakpoint-down(md) {
        width: rem(64px);
      }
    }
  }

  .content-header {
    padding: rem(12px);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    &-title {
      font-size: rem(20px);
      line-height: rem(24px);
      font-weight: 600;
      margin-bottom: 0;
    }

    .btn-apply {
      height: rem(24px);
      padding: 0 rem(8px);
    }
  }

  .content-body {
    padding: 0 rem(12px);
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__back {
    background: none;
    border: none;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    color: $gray-900;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(24px);
    height: rem(24px);
    margin-right: rem(12px);
  }

  &__close {
    border: none;
    margin: 0;
    padding: 0 rem(8px) 0 rem(4px);
    flex-shrink: 0;
    color: $gray-900;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $gray-100;
    height: rem(24px);
    border-radius: rem(3px);
  }

  &__close--icon {
    width: rem(24px);
    padding: 0;
    
    svg {
      width: rem(12px);
      height: rem(12px);
    }
  }
}
</style>
